import React, { useCallback } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { MDSButton, MDSButtonVariant, MDSButtonSlotName } from "@/mds/components/buttons";
import { MDSIconKind, MDSIcon } from "@/mds/components/icon";
import { SessionEventKind } from "../session.types";

interface MDSSessionFeedbackProps {
  /** The event ID to provide feedback for */
  eventId: string;
  /** The event kind to provide feedback for */
  eventKind: SessionEventKind;
  /** Handler function for feedback actions */
  onFeedback: (eventId: string, responseKind: "UPVOTE" | "DOWNVOTE") => void;
  /** Whether the feedback is selected */
  feedbackGiven?: "UPVOTE" | "DOWNVOTE" | null;
}

export const MDSSessionFeedback = observer<MDSSessionFeedbackProps>(function MDSSessionFeedback({
  eventId,
  onFeedback,
  feedbackGiven = null,
}) {
  const handleUpvoteClick = useCallback(() => {
    onFeedback(eventId, "UPVOTE");
  }, [eventId, onFeedback]);

  const handleDownvoteClick = useCallback(() => {
    onFeedback(eventId, "DOWNVOTE");
  }, [eventId, onFeedback]);

  return (
    <FeedbackContainer>
      <MDSButton
        variant={MDSButtonVariant.Transparent}
        onPress={handleUpvoteClick}
        aria-label="Mark response as good"
      >
        <MDSIcon
          slot={MDSButtonSlotName.LeftIcon}
          kind={feedbackGiven === "UPVOTE" ? MDSIconKind.UpvoteSolid : MDSIconKind.Upvote}
        />
      </MDSButton>
      <MDSButton
        variant={MDSButtonVariant.Transparent}
        onPress={handleDownvoteClick}
        aria-label="Mark response as bad"
      >
        <MDSIcon
          slot={MDSButtonSlotName.LeftIcon}
          kind={feedbackGiven === "DOWNVOTE" ? MDSIconKind.DownvoteSolid : MDSIconKind.Downvote}
        />
      </MDSButton>
    </FeedbackContainer>
  );
});

const FeedbackContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["1"]};
`;
